/**
 * Status Badge Registry
 *
 * Single source of truth for every admin status/category badge's color and
 * label. Replaces ~20 independently written status->color mappers that had
 * drifted onto conflicting palettes for the same meaning.
 *
 * Two axes:
 *  - `getStatusBadge(domain, value)` — a status progresses through one of six
 *    locked tones (success/progress/pending/attention/critical/special).
 *  - `getCategoryChip(domain, value)` — a fixed categorical color that isn't
 *    a status progression (document category, state, company source, etc).
 *
 * Unknown values never render raw — they fall back through
 * `humanizeBadgeLabel` (Title Case, no underscores).
 *
 * Customer-facing behavior is preserved by a delegating shim:
 * `features/federal-tax/domain/rules/status-display.rules.ts` calls into this
 * registry but keeps its existing exported names. State-filings statuses come
 * from `@nt/state-tax-rules`.
 */

import { STATUS_META } from '@nt/federal-tax-rules'
import { CLOSE_REASON_META, STATUS_META as STATE_TAX_STATUS_META } from '@nt/state-tax-rules'
import type { VariantProps } from 'class-variance-authority'

import type { badgeVariants } from '@/components/ui/badge'

export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>

// ============================================================================
// TONE TABLE (locked, 2026-07-14 admin listing design standard)
// ============================================================================

type BadgeTone = 'success' | 'progress' | 'pending' | 'attention' | 'critical' | 'special'

// A status can only ever resolve to one of these 6 colors — narrower than the
// full 26-variant BadgeVariant so status results stay assignable to existing
// narrow "urgency color" props (e.g. InfoCard's `borderColor`/`badgeColor`,
// which use this exact 6-value union).
export type ToneVariant = 'green' | 'blue' | 'zinc' | 'amber' | 'red' | 'purple'

const TONE_VARIANT: Record<BadgeTone, ToneVariant> = {
  success: 'green',
  progress: 'blue',
  pending: 'zinc',
  attention: 'amber',
  critical: 'red',
  special: 'purple',
}

interface StatusEntry {
  tone: BadgeTone
  label: string
}

interface StatusBadgeResult {
  variant: ToneVariant
  label: string
}

interface CategoryChipResult {
  variant: BadgeVariant
  label: string
}

// ============================================================================
// HUMANIZE FALLBACK
// ============================================================================

// Curated overrides for values arriving from data
// (e.g. staging /admin/documents chips) that don't title-case cleanly.
const HUMANIZE_OVERRIDES: Record<string, string> = {
  admin_adhoc_fax: 'Admin Ad Hoc Fax',
}

export function humanizeBadgeLabel(value: string | null | undefined): string {
  const raw = (value ?? '').toString().trim()
  if (!raw) return 'Unknown'

  const override = HUMANIZE_OVERRIDES[raw.toLowerCase()]
  if (override) return override

  return raw
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

// ============================================================================
// STATUS DOMAINS
// ============================================================================

type StatusDomain =
  | 'federalTax'
  | 'federalTaxDocStatus'
  | 'stateFilings'
  | 'registeredAgent'
  | 'registeredAgentAgreement'
  | 'registeredAgentFiling'
  | 'formation'
  | 'orderGeneric'
  | 'genericStatus'
  | 'genericPayment'
  | 'onboarding'
  | 'mailroomItemCompanies'
  | 'mailroomItemFeature'
  | 'mailroomOrder'
  | 'faxOutbound'
  | 'faxInbound'
  | 'webhookStatus'
  | 'stripeInvoice'
  | 'stripePaymentIntent'
  | 'stripeRefund'
  | 'stripeTransactionStatus'
  | 'stripeSubscription'
  | 'companyFederalTax'
  | 'companyPayments'
  | 'companyStateFiling'
  | 'companyTaxFilingDrawer'
  | 'dashboardFormationPhase'
  | 'dashboardActiveService'
  | 'contractorTaxForm'

// Several domains below have no Product-side consumer any more: their admin
// screens were deleted with the admin panel (goal 247) and the admin console
// (apps/admin) renders them now. The tables stay because they are the shared
// status vocabulary both apps read, and because the delegating shim named
// in this file's header still resolves customer statuses through them.
const FEDERAL_TAX_STATUS_ENTRIES: Record<string, StatusEntry> = Object.fromEntries(
  Object.entries(STATUS_META).map(([status, meta]) => [status, { tone: meta.tone, label: meta.customerLabel }]),
)

// State-filings order statuses from the shared registry (`@nt/state-tax-rules`
// STATUS_META, spec 2026-09-30 section 5.1): the customer label and the tone.
// A closed order reads its reason, keyed `closed:<reason>` (CLOSE_REASON_META).
const STATE_FILINGS_STATUS_ENTRIES: Record<string, StatusEntry> = {
  ...Object.fromEntries(
    Object.entries(STATE_TAX_STATUS_META).map(([status, meta]) => [
      status,
      { tone: meta.tone, label: meta.customerLabel },
    ]),
  ),
  ...Object.fromEntries(
    Object.entries(CLOSE_REASON_META).map(([reason, meta]) => [
      `closed:${reason}`,
      { tone: meta.tone, label: meta.customerLabel },
    ]),
  ),
}

const DOMAIN_STATUS: Record<StatusDomain, Record<string, StatusEntry>> = {
  // Federal-tax order statuses from the shared registry
  // (`@nt/federal-tax-rules` STATUS_META, spec 2026-09-26 section 8): the
  // customer label and the tone, so a status the API writes always resolves.
  federalTax: FEDERAL_TAX_STATUS_ENTRIES,

  // Federal-tax document status (was DOC_STATUS_COLORS in the deleted admin queue table).
  federalTaxDocStatus: {
    not_generated: { tone: 'pending', label: 'Not Generated' },
    awaiting_signature: { tone: 'attention', label: 'Awaiting Signature' },
    signed: { tone: 'success', label: 'Signed' },
  },

  // State filings (/state-filings): the v2 registry, `@nt/state-tax-rules`.
  stateFilings: STATE_FILINGS_STATUS_ENTRIES,

  // features/registered-agent/types/registered-agent.types.ts RAStatus.
  registeredAgent: {
    pending_setup: { tone: 'pending', label: 'Pending Setup' },
    pending_agreement: { tone: 'attention', label: 'Pending Agreement' },
    agreement_sent: { tone: 'progress', label: 'Agreement Sent' },
    agreement_signed: { tone: 'success', label: 'Agreement Signed' },
    state_filed: { tone: 'success', label: 'State Filed' },
    active: { tone: 'success', label: 'Active' },
    cancelled: { tone: 'pending', label: 'Cancelled' },
  },

  // Registered-agent agreement status.
  registeredAgentAgreement: {
    'not signed': { tone: 'pending', label: 'Not Signed' },
    'pending signature': { tone: 'attention', label: 'Pending Signature' },
    signed: { tone: 'success', label: 'Signed' },
    expired: { tone: 'critical', label: 'Expired' },
  },

  // Registered-agent state-filing status.
  registeredAgentFiling: {
    'not filed': { tone: 'pending', label: 'Not Filed' },
    'in progress': { tone: 'progress', label: 'In Progress' },
    filed: { tone: 'success', label: 'Filed' },
    accepted: { tone: 'success', label: 'Accepted' },
  },

  // features/formation/domain/value-objects/formation-status.vo.ts FormationStatus.
  // EIN sub-workflow (ein_ss4_sent..ein_awaiting_letter/ein_progress) is one
  // conceptual phase — kept a single progress/blue tone rather than splitting
  // on incidental words ("Signed", "Delivered") mid-pipeline, since none of
  // them represent the order's actual completion.
  formation: {
    pending: { tone: 'pending', label: 'Pending' },
    draft: { tone: 'pending', label: 'Draft' },
    verified: { tone: 'progress', label: 'Verified' },
    in_progress: { tone: 'progress', label: 'In Progress' },
    state_review: { tone: 'attention', label: 'State Review' },
    formation_done: { tone: 'progress', label: 'Formation Done' },
    ein_ss4_sent: { tone: 'progress', label: 'EIN SS-4 Sent' },
    ein_ss4_signed: { tone: 'progress', label: 'EIN SS-4 Signed' },
    ein_fax_sent: { tone: 'progress', label: 'EIN Fax Sent' },
    ein_fax_delivered: { tone: 'progress', label: 'EIN Fax Delivered' },
    ein_awaiting_letter: { tone: 'progress', label: 'EIN Awaiting Letter' },
    ein_progress: { tone: 'progress', label: 'EIN In Progress' },
    completed: { tone: 'success', label: 'Completed' },
    cancelled: { tone: 'pending', label: 'Cancelled' },
  },

  // Non-formation order status.
  orderGeneric: {
    active: { tone: 'success', label: 'Active' },
    completed: { tone: 'success', label: 'Completed' },
    pending: { tone: 'pending', label: 'Pending' },
    cancelled: { tone: 'pending', label: 'Cancelled' },
  },

  // Broad catch-all for order and user-profile cards —
  // broad catch-all — replaces badge-colors.ts's single-bucket getStatusBadgeVariant,
  // which lumped e.g. "in_progress" and "pending" onto the same yellow. Split
  // properly across the six tones here.
  genericStatus: {
    completed: { tone: 'success', label: 'Completed' },
    active: { tone: 'success', label: 'Active' },
    verified: { tone: 'success', label: 'Verified' },
    fulfilled: { tone: 'success', label: 'Fulfilled' },
    paid: { tone: 'success', label: 'Paid' },
    succeeded: { tone: 'success', label: 'Succeeded' },
    approved: { tone: 'success', label: 'Approved' },
    done: { tone: 'success', label: 'Done' },
    in_progress: { tone: 'progress', label: 'In Progress' },
    processing: { tone: 'progress', label: 'Processing' },
    waiting: { tone: 'progress', label: 'Waiting' },
    pending: { tone: 'pending', label: 'Pending' },
    unfulfilled: { tone: 'pending', label: 'Unfulfilled' },
    draft: { tone: 'pending', label: 'Draft' },
    not_started: { tone: 'pending', label: 'Not Started' },
    past_due: { tone: 'attention', label: 'Past Due' },
    inactive: { tone: 'pending', label: 'Inactive' },
    cancelled: { tone: 'pending', label: 'Cancelled' },
    canceled: { tone: 'pending', label: 'Cancelled' },
    failed: { tone: 'critical', label: 'Failed' },
    error: { tone: 'critical', label: 'Error' },
    expired: { tone: 'critical', label: 'Expired' },
    rejected: { tone: 'critical', label: 'Rejected' },
    refunded: { tone: 'special', label: 'Refunded' },
    trialing: { tone: 'progress', label: 'Trialing' },
    trial: { tone: 'progress', label: 'Trial' },
    new: { tone: 'pending', label: 'New' },
    info: { tone: 'pending', label: 'Info' },
  },

  // Generic payment status.
  genericPayment: {
    paid: { tone: 'success', label: 'Paid' },
    completed: { tone: 'success', label: 'Completed' },
    succeeded: { tone: 'success', label: 'Succeeded' },
    pending: { tone: 'pending', label: 'Pending' },
    processing: { tone: 'progress', label: 'Processing' },
    failed: { tone: 'critical', label: 'Failed' },
    cancelled: { tone: 'pending', label: 'Cancelled' },
    canceled: { tone: 'pending', label: 'Cancelled' },
    refunded: { tone: 'special', label: 'Refunded' },
  },

  // Company onboarding status (was renderOnboardingStatus).
  onboarding: {
    done: { tone: 'success', label: 'Done' },
    in_progress: { tone: 'progress', label: 'In Progress' },
    first_user: { tone: 'pending', label: 'First User' },
    first_login: { tone: 'pending', label: 'First Login' },
  },

  // Company mailroom item status (MailItemData['status']).
  mailroomItemCompanies: {
    pending: { tone: 'pending', label: 'Pending' },
    scanned: { tone: 'progress', label: 'Scanned' },
    forwarded: { tone: 'progress', label: 'Forwarded' },
    archived: { tone: 'pending', label: 'Archived' },
    shredded: { tone: 'critical', label: 'Shredded' },
  },

  // features/mailroom/config/mailroom.config.ts + types/mailroom.types.ts MailStatus.
  mailroomItemFeature: {
    received: { tone: 'success', label: 'Received' },
    scanned: { tone: 'progress', label: 'Scanned' },
    pending_action: { tone: 'attention', label: 'Pending Action' },
    forwarded: { tone: 'progress', label: 'Forwarded' },
    shredded: { tone: 'critical', label: 'Shredded' },
    archived: { tone: 'pending', label: 'Archived' },
  },

  // features/mailroom/lib/mailroom-status.service.ts MailroomStatus (subscription-level, not item-level).
  mailroomOrder: {
    active: { tone: 'success', label: 'Active' },
    pending: { tone: 'pending', label: 'Pending' },
    cancelled: { tone: 'pending', label: 'Cancelled' },
    expired: { tone: 'critical', label: 'Expired' },
  },

  // features/fax/domain/types.ts OutboundFaxStatus.
  faxOutbound: {
    queued: { tone: 'pending', label: 'Queued' },
    'media.processed': { tone: 'progress', label: 'Processed' },
    sending: { tone: 'progress', label: 'Sending' },
    delivered: { tone: 'success', label: 'Delivered' },
    failed: { tone: 'critical', label: 'Failed' },
    // Our send got no answer from the fax service (audit 2); the team checks it.
    unconfirmed: { tone: 'progress', label: 'Being checked' },
  },

  // features/fax/domain/types.ts InboundFaxStatus.
  faxInbound: {
    receiving: { tone: 'progress', label: 'Receiving' },
    'media.processing': { tone: 'progress', label: 'Processing' },
    received: { tone: 'success', label: 'Received' },
    failed: { tone: 'critical', label: 'Failed' },
  },

  // Webhook ProcessingStatus.
  webhookStatus: {
    processed: { tone: 'success', label: 'Processed' },
    failed: { tone: 'critical', label: 'Failed' },
    processing: { tone: 'progress', label: 'Processing' },
    pending: { tone: 'pending', label: 'Pending' },
    retry: { tone: 'attention', label: 'Retrying' },
  },

  // Stripe invoice.status.
  stripeInvoice: {
    paid: { tone: 'success', label: 'Paid' },
    open: { tone: 'attention', label: 'Open' },
    void: { tone: 'pending', label: 'Void' },
    draft: { tone: 'pending', label: 'Draft' },
    uncollectible: { tone: 'critical', label: 'Uncollectible' },
  },

  // Stripe PaymentIntent.status.
  stripePaymentIntent: {
    succeeded: { tone: 'success', label: 'Succeeded' },
    canceled: { tone: 'pending', label: 'Canceled' },
    processing: { tone: 'progress', label: 'Processing' },
    requires_payment_method: { tone: 'attention', label: 'Requires Payment Method' },
    requires_confirmation: { tone: 'attention', label: 'Requires Confirmation' },
    requires_action: { tone: 'attention', label: 'Requires Action' },
  },

  // Stripe Refund.status.
  stripeRefund: {
    succeeded: { tone: 'success', label: 'Succeeded' },
    pending: { tone: 'pending', label: 'Pending' },
    failed: { tone: 'critical', label: 'Failed' },
    canceled: { tone: 'pending', label: 'Canceled' },
  },

  // Internal financial-transaction status.
  stripeTransactionStatus: {
    completed: { tone: 'success', label: 'Completed' },
    pending: { tone: 'pending', label: 'Pending' },
    failed: { tone: 'critical', label: 'Failed' },
    reversed: { tone: 'attention', label: 'Reversed' },
  },

  // Stripe Subscription.status.
  stripeSubscription: {
    active: { tone: 'success', label: 'Active' },
    past_due: { tone: 'attention', label: 'Past Due' },
    canceled: { tone: 'pending', label: 'Canceled' },
    trialing: { tone: 'progress', label: 'Trialing' },
    unpaid: { tone: 'critical', label: 'Unpaid' },
    incomplete: { tone: 'attention', label: 'Incomplete' },
    paused: { tone: 'special', label: 'Paused' },
    other: { tone: 'pending', label: 'Other' },
  },

  // Company federal-tax display union.
  companyFederalTax: {
    filed: { tone: 'success', label: 'Filed' },
    available: { tone: 'success', label: 'Available' },
    pending: { tone: 'pending', label: 'Pending' },
    processing: { tone: 'progress', label: 'Processing' },
    upcoming: { tone: 'pending', label: 'Upcoming' },
    overdue: { tone: 'critical', label: 'Overdue' },
    rejected: { tone: 'critical', label: 'Rejected' },
    'not required': { tone: 'pending', label: 'Not Required' },
    'due soon': { tone: 'attention', label: 'Due Soon' },
  },

  // Company payments status.
  companyPayments: {
    completed: { tone: 'success', label: 'Completed' },
    pending: { tone: 'pending', label: 'Pending' },
    failed: { tone: 'critical', label: 'Failed' },
    refunded: { tone: 'special', label: 'Refunded' },
    disputed: { tone: 'attention', label: 'Disputed' },
  },

  // Company state-filing display union.
  companyStateFiling: {
    filed: { tone: 'success', label: 'Filed' },
    due: { tone: 'attention', label: 'Due' },
    overdue: { tone: 'critical', label: 'Overdue' },
    'not required': { tone: 'pending', label: 'Not Required' },
  },

  // Company tax-filing detail-drawer status.
  companyTaxFilingDrawer: {
    filed: { tone: 'success', label: 'Filed' },
    in_progress: { tone: 'progress', label: 'In Progress' },
    pending: { tone: 'pending', label: 'Pending' },
    rejected: { tone: 'critical', label: 'Rejected' },
  },

  // features/dashboard/ui/formation-phase-card.tsx PhaseStatus.
  dashboardFormationPhase: {
    completed: { tone: 'success', label: 'Completed' },
    in_progress: { tone: 'progress', label: 'In Progress' },
    upcoming: { tone: 'pending', label: 'Upcoming' },
  },

  // features/dashboard/ui/services-dashboard/active-services-compact.tsx ActiveService['status'].
  dashboardActiveService: {
    active: { tone: 'success', label: 'Active' },
    pending: { tone: 'pending', label: 'Pending' },
    expired: { tone: 'critical', label: 'Expired' },
    completed: { tone: 'success', label: 'Completed' },
  },

  // features/accounting/lib/api-contacts.ts ContractorFormStatus (contractor W-9 / W-8 forms).
  contractorTaxForm: {
    needed: { tone: 'attention', label: 'Form needed' },
    requested: { tone: 'progress', label: 'Requested' },
    on_file: { tone: 'success', label: 'On file' },
    expiring: { tone: 'attention', label: 'Expiring soon' },
    expired: { tone: 'critical', label: 'Expired' },
    not_needed: { tone: 'pending', label: 'Not needed' },
  },
}

/**
 * Resolve a status value to its badge variant + label for a given domain.
 * Unknown values fall back to the pending tone with a humanized label —
 * never render a raw snake_case/lowercase value.
 */
export function getStatusBadge(domain: StatusDomain, value: string | null | undefined): StatusBadgeResult {
  const raw = (value ?? '').toString()
  const table = DOMAIN_STATUS[domain]
  const entry = table[raw.toLowerCase()] ?? table[raw]

  if (entry) {
    return { variant: TONE_VARIANT[entry.tone], label: entry.label }
  }

  return { variant: TONE_VARIANT.pending, label: humanizeBadgeLabel(raw) }
}

// ============================================================================
// CATEGORY DOMAINS (fixed categorical colors, not a status progression)
// ============================================================================

interface ChipEntry {
  variant: BadgeVariant
  label: string
}

type CategoryDomain =
  | 'documentCategory'
  | 'blockedOn'
  | 'state'
  | 'scenario'
  | 'companySource'
  | 'webhookSource'
  | 'mailType'
  | 'stripeTransactionType'
  | 'notificationCategory'

const DOMAIN_CATEGORY: Record<CategoryDomain, Record<string, ChipEntry>> = {
  // types/enums.ts CompanyDocumentCategory. Values match the locked chips
  // (EIN=blue, Formation=green, Tax=amber, Others=zinc); Operation/S83b were
  // not locked by the goal so the prior colors are preserved unchanged.
  documentCategory: {
    ein: { variant: 'blue', label: 'EIN' },
    formation: { variant: 'green', label: 'Formation' },
    operation: { variant: 'purple', label: 'Operation' },
    s83b: { variant: 'orange', label: '83(b)' },
    tax: { variant: 'amber', label: 'Tax' },
    others: { variant: 'zinc', label: 'Others' },
  },

  // The admin work queue's "Waiting on" axis — `blockedOn` from
  // apps/api/src/orders/work-items.ts. A fixed three-value map rather
  // than a progression: an order does not move "through" these, it is waiting
  // on exactly one party. `us` carries the attention colour because it is the
  // only value an admin can clear by acting; the other two are information.
  blockedOn: {
    us: { variant: 'amber', label: 'Us' },
    customer: { variant: 'blue', label: 'Customer' },
    third_party: { variant: 'purple', label: 'Third party' },
  },

  // Locked: DE=indigo, WY=teal.
  state: {
    de: { variant: 'indigo', label: 'DE' },
    wy: { variant: 'teal', label: 'WY' },
  },

  // Locked: scenario chips (SMLLC, C-Corp) = sky.
  scenario: {
    smllc: { variant: 'sky', label: 'SMLLC' },
    mmllc: { variant: 'sky', label: 'MMLLC' },
    'c-corp': { variant: 'sky', label: 'C-Corp' },
    ccorp: { variant: 'sky', label: 'C-Corp' },
  },

  // types/enums.ts CompanySource. Locked: Existing Company=orange, Formed By Us=green.
  companySource: {
    existing_company: { variant: 'orange', label: 'Existing' },
    formed_by_us: { variant: 'green', label: 'By Us' },
  },

  // Webhook source (was getSourceColor) — fixed
  // map, unchanged per goal ("webhook sources keep one fixed map").
  webhookSource: {
    stripe: { variant: 'blue', label: 'Stripe' },
    docuseal: { variant: 'green', label: 'DocuSeal' },
    auth: { variant: 'purple', label: 'Auth' },
    telnyx: { variant: 'zinc', label: 'Telnyx' },
    lob: { variant: 'amber', label: 'Lob' },
    plaid: { variant: 'orange', label: 'Plaid' },
  },

  // Mail type chip (was getMailTypeBadgeVariant).
  mailType: {
    letter: { variant: 'outline', label: 'Letter' },
    package: { variant: 'resources', label: 'Package' },
    legal: { variant: 'destructive', label: 'Legal' },
    government: { variant: 'destructive', label: 'Government' },
    marketing: { variant: 'secondary', label: 'Marketing' },
    other: { variant: 'outline', label: 'Other' },
  },

  // Stripe transaction type (was TYPE_COLORS).
  stripeTransactionType: {
    payment: { variant: 'default', label: 'Payment' },
    refund: { variant: 'destructive', label: 'Refund' },
    adjustment: { variant: 'secondary', label: 'Adjustment' },
    credit: { variant: 'secondary', label: 'Credit' },
  },

  // Notification category (CategoryKey).
  // documents was `cyan`, not one of the locked categorical colors — mapped
  // to `sky` instead (matches the scenario chip's locked sky); every other
  // category keeps its prior color.
  notificationCategory: {
    account: { variant: 'purple', label: 'Account' },
    orders: { variant: 'blue', label: 'Orders' },
    formation: { variant: 'green', label: 'Formation' },
    tax: { variant: 'amber', label: 'Tax' },
    documents: { variant: 'sky', label: 'Documents' },
    billing: { variant: 'red', label: 'Billing' },
    services: { variant: 'zinc', label: 'Services' },
  },
}

// webhookSource's prior default fallback was 'yellow' (not a status tone —
// preserved verbatim since this axis is a fixed categorical map, not part of
// the locked six-tone system).
const CATEGORY_UNKNOWN_FALLBACK: Partial<Record<CategoryDomain, BadgeVariant>> = {
  webhookSource: 'yellow',
}

/**
 * Resolve a category value to its fixed badge variant + label for a given
 * domain. Unknown values fall back to zinc (or the domain's documented
 * legacy default) with a humanized label — never render a raw value.
 */
export function getCategoryChip(domain: CategoryDomain, value: string | null | undefined): CategoryChipResult {
  const raw = (value ?? '').toString()
  const table = DOMAIN_CATEGORY[domain]
  const entry = table[raw.toLowerCase()] ?? table[raw]

  if (entry) {
    return entry
  }

  return { variant: CATEGORY_UNKNOWN_FALLBACK[domain] ?? 'zinc', label: humanizeBadgeLabel(raw) }
}
